import React from 'react';

/**
 * "Why are you cancelling your trial?" — the one exit interview we get.
 *
 * Same shell as PricingModal (backdrop click, ×, Escape, focus in/out) so the
 * two dialogs feel like one product. Nothing here talks to the server: the
 * parent owns the request, so `busy` and `error` arrive as props and the only
 * thing this component decides is what was ticked.
 *
 * Since 2026-09 the same sheet also ends a paid membership's renewal — the
 * owner wanted the two exits to feel identical, and they are: the same
 * reasons, the same table behind them. Only the words change, chosen by
 * `kind`. The file keeps its trial name because renaming it would ripple
 * through imports for no behavioural gain; the wording lives in COPY below.
 */

export type CancelKind = 'trial' | 'subscription';

interface CancelTrialModalProps {
  /** Server-authored list, "Other" last; the parent supplies the fallback. */
  reasons: string[];
  busy: boolean;
  error: string;
  onClose: () => void;
  onConfirm: (reasons: string[], other: string) => void;
  /** Which exit this is. Defaults to the trial so the original call site is
   *  unchanged. */
  kind?: CancelKind;
  /** Membership only: the already-paid-for date access runs to. Printed under
   *  the title because the one thing people fear at this button is losing
   *  what they paid for today. */
  accessUntil?: string;
}

const OTHER = 'Other';

/** Everything that differs between the two exits, in one place. */
const COPY: Record<CancelKind, { title: string; keep: string; confirm: string }> = {
  trial: {
    title: 'Why are you cancelling your trial?',
    keep: 'Keep my trial',
    confirm: 'Cancel my Trial',
  },
  subscription: {
    title: 'Why are you cancelling your membership?',
    keep: 'Keep my membership',
    confirm: 'Cancel my membership',
  },
};

const CancelTrialModal: React.FC<CancelTrialModalProps> = ({
  reasons, busy, error, onClose, onConfirm, kind = 'trial', accessUntil,
}) => {
  const copy = COPY[kind];
  const [picked, setPicked] = React.useState<string[]>([]);
  const [other, setOther] = React.useState('');
  // Closing mid-request is ignored rather than cancelling it: the server may
  // already have ended the trial, and a modal that vanished before the answer
  // came back would leave the tab showing a trial that no longer exists.
  const close = () => { if (!busy) onClose(); };
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const closeFnRef = React.useRef(close);
  closeFnRef.current = close;
  React.useEffect(() => {
    const previously = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeFnRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previously?.focus?.();
    };
  }, []);

  const toggle = (r: string) =>
    setPicked(p => p.includes(r) ? p.filter(x => x !== r) : [...p, r]);
  const otherOn = picked.includes(OTHER);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
         onClick={close}>
      <div role="dialog" aria-modal="true" aria-labelledby="cancel-trial-title"
           className="relative bg-white rounded-2xl shadow-lg max-w-md w-full max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <button ref={closeRef} type="button" onClick={close} aria-label="Close"
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full text-grayscale-1100 hover:bg-grayscale-200 text-xl leading-none">
          ×
        </button>
        <div className="p-5 sm:p-6 overflow-y-auto">
          <h2 id="cancel-trial-title" className="text-xl font-bold text-grayscale-1300 font-grotesk pr-8">
            {copy.title}
          </h2>
          {/* Only the membership exit has something paid-for to reassure
              about; a trial's "you won't be charged" is said by the tab after
              the fact, where the date is known. */}
          {kind === 'subscription' && accessUntil && (
            <p className="text-xs text-grayscale-1100 mt-1">
              You keep access until {accessUntil}. Nothing further will be charged.
            </p>
          )}
          <p className="text-[13px] text-grayscale-1200 mt-1 mb-3">Select all that apply.</p>

          <div className="flex flex-col">
            {reasons.map(r => (
              <label key={r}
                className="flex items-start gap-3 py-2 border-b border-grayscale-200 last:border-b-0 cursor-pointer">
                <input type="checkbox" checked={picked.includes(r)} disabled={busy}
                  className="mt-0.5 w-4 h-4 shrink-0"
                  onChange={() => toggle(r)} />
                <span className="text-[13px] text-grayscale-1300">{r}</span>
              </label>
            ))}
          </div>

          {otherOn && (
            <textarea value={other} rows={3} maxLength={1000} disabled={busy}
              onChange={e => setOther(e.target.value)}
              placeholder="Tell us more (optional)"
              className="mt-3 w-full px-3 py-2 rounded-lg border border-grayscale-300 bg-white text-[13px] focus:border-primary-500 outline-none resize-none" />
          )}

          {error && <div className="text-xs text-red-600 mt-3">{error}</div>}

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button type="button" onClick={close} disabled={busy}
              className="flex-1 py-3 px-5 rounded-xl border border-grayscale-300 text-grayscale-1300 text-sm font-medium hover:border-primary-500 disabled:opacity-50">
              {copy.keep}
            </button>
            <button type="button"
              // Text typed under "Other" only counts while "Other" is ticked;
              // unticking it retracts the note without making them clear it.
              onClick={() => onConfirm(picked, otherOn ? other.trim() : '')}
              disabled={busy || !picked.length}
              className="flex-1 py-3 px-5 rounded-xl font-grotesk text-sm font-semibold bg-primary-600 text-grayscale-100 hover:bg-primary-700 transition-colors disabled:bg-primary-100 disabled:text-primary-300">
              {busy ? 'Cancelling…' : copy.confirm}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelTrialModal;
